import axios from "axios";
import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} TeacherStatusData
 * @property {number} id - ID of the teacher
 * @property {string} tenant_id - ID of the tenant
 * @property {string|null} inst_id - Institution ID
 * @property {string} firstName - First name of the teacher
 * @property {string} lastName - Last name of the teacher
 * @property {string} email - Email address of the teacher
 * @property {number} role_id - Role ID of the teacher
 * @property {string} reg_type - Registration type
 * @property {string} status - Status of the teacher ("0" for inactive, "1" for active)
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 * @property {string} teacher_code - Unique teacher code
 */

/**
 * Custom hook to toggle a teacher's active/inactive status.
 * This hook provides functionality to activate or deactivate a teacher account.
 */

export const useToggleTeacherStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [updatedTeacher, setUpdatedTeacher] = useState(
    /** @type {TeacherStatusData | null} */ (null),
  );

  /**
   * Toggles the teacher's status (active/inactive)
   * @param {string} teacherId - The ID of the teacher whose status should be toggled
   * @param {() => void} [onSuccess] - Optional callback to run on success
   * @returns {Promise<void>}
   */

  const { axiosInstance } = useAxios();

  const toggleStatus = async (teacherId, onSuccess) => {
    setLoading(true);
    setError("");
    setSuccess(false);
    setStatusMessage("");
    setUpdatedTeacher(null);

    try {
      const response = await axiosInstance.patch(
        `${TENANT_DOMAIN}/school/admin/teacher/${teacherId}/toggle-status`,
        {},
      );

      const responseData = response.data;

      setSuccess(true);
      setStatusMessage(responseData.message || "Status updated successfully");
      setUpdatedTeacher(responseData.data);

      if (onSuccess) {
        onSuccess(responseData);
      }
    } catch (err) {
      if (error.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(error?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    toggleStatus,
    loading,
    error,
    success,
    statusMessage,
    updatedTeacher,
  };
};
